import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import ModalForm from "../../../../components/compounds/modalForm/ModalForm.compunds"
import Divider from "../../../../components/atoms/Divider.atom"
import GridContainer from "../../../../components/layouts/containers/GridContainer.container"
import GridItem from "../../../../components/layouts/containers/GridItem.container"
import { ValueWithLabelTemplate } from "../../../../components/layouts/containers/ValueWithLabelElements.container"
import {
  getVacationRangeLabel,
  getVacationState,
  isForeignSchema,
} from "../../../../utils/vacation.utils"
import VacationFlag from "../../../../components/molecules/VacationFlag.molecule"
import { DateTime } from "luxon"
import VacationDayTypesBreakdown from "../../components/VacationDayTypesBreakdown.molecule"
import PrimaryButton from "../../../../components/molecules/buttons/PrimaryButton.molecule"
import DangerButton from "../../../../components/molecules/buttons/DangerButton.molecule"
import {
  approveCancelVacationRequest,
  getLastApprovedVacationRangeByVacationId,
  rejectCancelVacationRequest,
} from "../../../../services/vacation.service"
import useToast from "../../../../hooks/useToast.hook"
import VacationStatusChip from "../../../../components/molecules/VacationStatusChip.molecule"
import {
  VACATIONS_APPROVE_CANCEL_VACATION_REQUEST_SUCCESS,
  VACATIONS_APPROVE_CHANGE_VACATION_REQUEST_SUCCESS,
  VACATIONS_REJECT_CHANGE_VACATION_REQUEST_SUCCESS,
} from "../../../../consts/modules/vacations.consts"

const ModalFormCancelRequestReview = ({
  visible,
  setVisible,
  selectedVacationRequest,
  triggerRefresh,
}) => {
  const [submitting, setSubmitting] = useState({
    approve: false,
    reject: false,
  })
  const [lastApprovedVacationRequest, setLastApprovedVacationRequest] =
    useState()
  const { setSuccessMessage, setErrorMessage } = useToast()

  useEffect(() => {
    if (visible && selectedVacationRequest) {
      fetchLastApprovedVacationRequest()
    }
  }, [selectedVacationRequest])
  const fetchLastApprovedVacationRequest = async () => {
    try {
      const response = await getLastApprovedVacationRangeByVacationId(
        selectedVacationRequest.idVacations
      )
      if (!response.success) {
        throw new Error(response.message)
      }
      setLastApprovedVacationRequest(response.result[0])
    } catch (error) {
      setErrorMessage({
        title: "Error",
        message: error.message,
      })
    }
  }
  const setApproving = (value) => {
    setSubmitting((prev) => ({
      ...prev,
      approve: value,
    }))
  }
  const setRejecting = (value) => {
    setSubmitting((prev) => ({
      ...prev,
      reject: value,
    }))
  }
  const handleRejectSubmit = async () => {
    setRejecting(true)
    try {
      await rejectCancelVacationRequest(selectedVacationRequest.idVacations)
      triggerRefresh()
      setSuccessMessage(VACATIONS_REJECT_CHANGE_VACATION_REQUEST_SUCCESS)
    } catch (error) {
      setErrorMessage({
        title: "Error",
        message: error.message,
      })
    } finally {
      setSubmitting(false)
      setVisible(false)
    }
  }
  const handleApproveSubmit = async () => {
    setApproving(true)
    try {
      await approveCancelVacationRequest(
        selectedVacationRequest.idVacations,
        lastApprovedVacationRequest.vacationRangeId
      )
      triggerRefresh()
      setSuccessMessage(VACATIONS_APPROVE_CANCEL_VACATION_REQUEST_SUCCESS)
    } catch (error) {
      setErrorMessage({
        title: "Error",
        message: error.message,
      })
    } finally {
      setSubmitting(false)
      setVisible(false)
    }
  }
  const modalFooterTemplate = () => {
    return (
      <div>
        <DangerButton
          label="Rechazar"
          btnType="outlined"
          icon={`pi ${submitting.reject ? "pi-spin pi-spinner" : "pi-times"}`}
          onClick={handleRejectSubmit}
          disabled={submitting.reject}
          rounded
        />
        <PrimaryButton
          label="Aprobar"
          icon={`pi ${submitting.approve ? "pi-spin pi-spinner" : "pi-check"}`}
          onClick={handleApproveSubmit}
          disabled={submitting.approve}
          rounded
        />
      </div>
    )
  }

  if (!selectedVacationRequest || !lastApprovedVacationRequest) {
    return null
  }
  return (
    <>
      <ModalForm
        className="vacation-change-review-modal-form"
        headerTitle="Solicitud de cancelación"
        headerIconClass="pi pi-ban clr-danger"
        footer={modalFooterTemplate()}
        visible={visible}
        setVisible={setVisible}
        dismissableMask
        closable
      >
        <GridContainer className="vacation-request-summary">
          <GridItem col={6}>
            <ValueWithLabelTemplate
              label="Integrante"
              value={
                <div className="flex gap-2 align-items-center">
                  <span>{selectedVacationRequest.teamMember.fullName}</span>
                  <VacationFlag
                    schemaTitle={
                      selectedVacationRequest.teamMember.holidaySchemaName
                    }
                  />
                </div>
              }
            />
          </GridItem>
          <GridItem col={6}>
            <ValueWithLabelTemplate
              label="Fecha de la solicitud"
              value={DateTime.fromISO(
                selectedVacationRequest.createdAt
              ).toFormat("dd/MM/yyyy")}
            />
          </GridItem>
          <GridItem col={6}>
            <ValueWithLabelTemplate
              label="Razón de la cancelación"
              value={selectedVacationRequest.cancelationMotive}
            />
          </GridItem>
        </GridContainer>
        <Divider stroke="sm" />
        <VacationVersionDetails vacationRequest={lastApprovedVacationRequest} />
        <Divider stroke="sm" />
        <VacationStatusDetails vacationRequest={lastApprovedVacationRequest} />
      </ModalForm>
    </>
  )
}

const VacationVersionDetails = ({ vacationRequest }) => {
  return (
    <GridContainer className="state-form flex-1">
      <GridItem col={12}>
        <span className="value font-cairo font-semibold text-xl line-height-1">
          Detalles
        </span>
      </GridItem>
      <GridItem col={6}>
        <ValueWithLabelTemplate
          label="Fechas solicitadas"
          value={getVacationRangeLabel(
            vacationRequest.numberOfVacationDays,
            vacationRequest.startDate,
            vacationRequest.endDate,
            {
              includeNumberOfDays: false,
              includeDateRange: true,
              dateFormat: "dd/MM/yyyy",
            }
          )}
        />
      </GridItem>
      <GridItem col={6}>
        <ValueWithLabelTemplate
          label="Cantidad de días"
          value={
            <div className="flex gap-2">
              {vacationRequest?.numberOfVacationDays}
              {!isForeignSchema(vacationRequest.teamMember.idHolidaySchema) && (
                <VacationDayTypesBreakdown vacationRequest={vacationRequest} />
              )}
            </div>
          }
        />
      </GridItem>
      <GridItem col={6}>
        <ValueWithLabelTemplate
          label="Motivo"
          value={vacationRequest.vacationsMotive}
        />
      </GridItem>
    </GridContainer>
  )
}

const VacationStatusDetails = ({ vacationRequest }) => {
  return (
    <GridContainer className="state-form">
      <GridItem col={6}>
        <ValueWithLabelTemplate
          label="Estado"
          value={
            <VacationStatusChip
              status={getVacationState(vacationRequest)}
              variant="long"
            />
          }
        />
      </GridItem>
      <GridItem col={6}>
        <ValueWithLabelTemplate
          label="Responsable"
          value={vacationRequest?.approver?.fullName}
        />
      </GridItem>
      {vacationRequest?.plusDaysPayDate && (
        <GridItem col={6}>
          <ValueWithLabelTemplate
            label="Fecha de pago"
            value={DateTime.fromISO(vacationRequest.plusDaysPayDate).toFormat(
              "dd/MM/yyyy"
            )}
          />
        </GridItem>
      )}
    </GridContainer>
  )
}

export default ModalFormCancelRequestReview

ModalFormCancelRequestReview.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  selectedVacationRequest: PropTypes.object,
}
